/**
 * A wall-clock timestamp that refreshes on an interval.
 *
 * Exists because several things on screen genuinely depend on the current time rather
 * than on fetched data: an open alarm's duration, "pending for 4h", the `now` marker on a
 * timeline. Reading `Date.now()` during render makes those values stale until the next
 * unrelated re-render — an alarm that opened two minutes ago keeps reading "just now"
 * until something else happens to update.
 *
 * The alternative that was tried first — `useMemo(() => Date.now(), [query.data])` — ties
 * the clock to a data refetch, which is both wrong (the clock is not data) and something
 * the linter correctly flags.
 *
 * Default cadence is 60s: fine for durations rendered in minutes, and cheap enough that
 * several components can each hold one.
 */
import { useEffect, useState } from 'react';

export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return now;
}
